import { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import type {
  Building,
  BuildingHours,
  GuestInvitationDto,
  RecentGuest,
  RoomBookingDto,
} from '@hub-wifi/shared'
import { apiRequest } from '../../api/client'
import { listGuestInvitations, listRecentGuests } from '../../api/guestInvitations'

/** Enough for "next booking" and the bookings the guest list nests under. */
const BOOKINGS_FETCH_LIMIT = 20

export interface GuestData {
  loading: boolean
  /** `'GENERIC_ERROR'` or an API message. Only the primary request fails the page. */
  error: string | null
  timeZone: string
  hours: BuildingHours[]
  recent: RecentGuest[]
  setRecent: (recent: RecentGuest[]) => void
  bookings: RoomBookingDto[]
  invitations: GuestInvitationDto[]
}

function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/**
 * Everything the guest pages need in one go: building (timezone + hours),
 * recent guests, upcoming bookings and, for the list, the invitations.
 * Bookings are best-effort (no bookings just means no "With my booking").
 */
export function useGuestData(options: { invitations: boolean }): GuestData {
  const { getAccessTokenSilently } = useAuth0()
  const wantInvitations = options.invitations
  const [state, setState] = useState<Omit<GuestData, 'setRecent'>>({
    loading: true,
    error: null,
    timeZone: browserTimeZone(),
    hours: [],
    recent: [],
    bookings: [],
    invitations: [],
  })

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const token = await getAccessTokenSilently()
        const qs = new URLSearchParams({
          timeframe: 'upcoming',
          limit: String(BOOKINGS_FETCH_LIMIT),
        })
        const [recent, invitations, buildingRes, bookingsRes] = await Promise.all([
          listRecentGuests(token),
          wantInvitations ? listGuestInvitations(token) : Promise.resolve([]),
          apiRequest<{ building: Building | null }>('/api/my/building', { token }),
          apiRequest<{ items: RoomBookingDto[] }>(`/api/my/bookings?${qs}`, { token }).catch(
            () => ({ items: [] as RoomBookingDto[] }),
          ),
        ])
        if (cancelled) return
        const b = buildingRes.building
        setState((s) => ({
          ...s,
          loading: false,
          recent,
          invitations,
          bookings: bookingsRes.items ?? [],
          timeZone: b?.timezone ?? s.timeZone,
          hours: b?.hours ?? [],
        }))
      } catch (err) {
        if (cancelled) return
        setState((s) => ({
          ...s,
          loading: false,
          error: err instanceof Error ? err.message : 'GENERIC_ERROR',
        }))
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [getAccessTokenSilently, wantInvitations])

  return { ...state, setRecent: (recent) => setState((s) => ({ ...s, recent })) }
}
